import React from 'react';
import { Link } from 'react-router-dom';
import { Home, Shield } from 'lucide-react';
import Header from '../components/Header';

const Section = ({ title, children }) => (
  <div className="mb-8">
    <h2 className="text-lg font-heading font-semibold text-foreground mb-3">{title}</h2>
    <div className="text-muted-foreground text-sm leading-relaxed space-y-3">{children}</div>
  </div>
);

const PrivacyPolicy = () => {
  const lastUpdated = 'March 3, 2026';

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />

      <div className="pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-6">
          {/* Header */}
          <div className="mb-10">
            <div className="flex items-center gap-2 text-primary mb-4">
              <Shield className="h-5 w-5" />
              <span className="text-sm font-medium uppercase tracking-wider">Legal</span>
            </div>
            <h1 className="text-4xl font-heading font-bold mb-2" data-testid="privacy-policy-title">
              Privacy Policy
            </h1>
            <p className="text-muted-foreground text-sm">Last updated: {lastUpdated}</p>
          </div>

          <div className="bg-card border border-border rounded-xl p-8 md:p-10">
            <Section title="1. Introduction">
              <p>
                Kinetic Analytics Inc. ("we", "our", or "us") operates My Mortgage Canada
                (mymortgagecanada.app). This Privacy Policy explains how we collect, use,
                disclose, and safeguard your information when you use our mortgage renewal
                dashboard service. By using the Service you agree to the practices described
                here.
              </p>
            </Section>

            <Section title="2. Information We Collect">
              <p><strong className="text-foreground">Account Information.</strong> When you sign in with Google OAuth we receive your name, email address, and profile picture from Google. We do not receive or store your Google password.</p>
              <p><strong className="text-foreground">Mortgage Details.</strong> Information you voluntarily enter such as mortgage principal, interest rate, amortization period, renewal date, home value, province, and financial goals.</p>
              <p><strong className="text-foreground">Payment Information.</strong> Subscription payments are processed by Stripe. We do not store credit card numbers. Stripe may collect billing details in accordance with their own privacy policy.</p>
              <p><strong className="text-foreground">Phone Number.</strong> If you opt in to SMS notifications we collect and verify your phone number via Twilio.</p>
              <p><strong className="text-foreground">Usage Data.</strong> We collect anonymized analytics events (page views, feature usage) through Google Analytics to improve the Service.</p>
              <p><strong className="text-foreground">Support Tickets.</strong> Messages you send through our priority support feature are stored to provide customer assistance.</p>
            </Section>

            <Section title="3. How We Use Your Information">
              <p>We use collected information to:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Provide, operate, and maintain the Service</li>
                <li>Calculate mortgage payments, amortization schedules, and renewal projections</li>
                <li>Send rate alerts, renewal reminders, and weekly digest emails you have opted in to</li>
                <li>Send SMS notifications for rate alerts and renewal reminders (if enabled)</li>
                <li>Process subscription payments through Stripe</li>
                <li>Generate personalized renewal action plans and HELOC analyses</li>
                <li>Display aggregated, publicly available mortgage rate data scraped from public sources (Bank of Canada, RateHub.ca, WOWA.ca)</li>
                <li>Improve and personalize the Service through anonymized analytics</li>
                <li>Respond to support requests</li>
              </ul>
            </Section>

            <Section title="4. Third-Party Services">
              <p>We integrate with the following third-party services, each governed by their own privacy policies:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong className="text-foreground">Google OAuth</strong> — Authentication and sign-in</li>
                <li><strong className="text-foreground">Stripe</strong> — Payment processing for subscriptions</li>
                <li><strong className="text-foreground">Twilio</strong> — SMS notifications and phone verification</li>
                <li><strong className="text-foreground">Resend</strong> — Transactional and digest emails</li>
                <li><strong className="text-foreground">Google Analytics</strong> — Anonymized usage analytics</li>
              </ul>
              <p>We do not sell your personal information to any third party.</p>
            </Section>

            <Section title="5. Data Storage and Security">
              <p>
                Your data is stored in a secure MongoDB database hosted in a cloud environment.
                We use industry-standard security measures including encrypted connections (HTTPS/TLS),
                token-based authentication, and access controls to protect your information.
              </p>
              <p>
                Authentication tokens are stored in your browser's local storage to maintain your
                session. No sensitive financial data (e.g., credit card numbers) is stored on our
                servers.
              </p>
            </Section>

            <Section title="6. Data Retention">
              <p>
                We retain your account data for as long as your account is active. If you delete
                your account, we will remove your personal data from our systems within 30 days,
                except where retention is required by law or for legitimate business purposes
                (e.g., fraud prevention, financial records).
              </p>
              <p>Session tokens expire after 7 days and are automatically purged.</p>
            </Section>

            <Section title="7. Your Rights">
              <p>Depending on your jurisdiction, you may have the right to:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Access the personal data we hold about you</li>
                <li>Request correction of inaccurate data</li>
                <li>Request deletion of your account and associated data</li>
                <li>Opt out of marketing emails and SMS notifications at any time</li>
                <li>Withdraw consent for data processing</li>
              </ul>
              <p>
                Canadian residents have additional rights under PIPEDA (Personal Information
                Protection and Electronic Documents Act). To exercise any of these rights,
                contact us at the email below.
              </p>
            </Section>

            <Section title="8. Cookies and Local Storage">
              <p>
                We use browser local storage to maintain your authentication session and user
                preferences. We do not use tracking cookies. Third-party services (e.g., Google
                Analytics, Stripe) may set their own cookies as described in their respective
                privacy policies.
              </p>
            </Section>

            <Section title="9. Children's Privacy">
              <p>
                The Service is not intended for individuals under the age of 18. We do not
                knowingly collect personal information from children. If you believe we have
                inadvertently collected such information, please contact us immediately.
              </p>
            </Section>

            <Section title="10. Changes to This Policy">
              <p>
                We may update this Privacy Policy from time to time. We will notify you of
                material changes by posting the new policy on this page and updating the "Last
                updated" date. Continued use of the Service after changes constitutes acceptance
                of the revised policy.
              </p>
            </Section>

            <Section title="11. Contact Us">
              <p>If you have questions about this Privacy Policy or your personal data, contact us at:</p>
              <div className="bg-background/50 border border-border rounded-lg p-4 mt-2">
                <p className="text-foreground font-medium">Kinetic Analytics Inc.</p>
                <p>Email: <a href="mailto:info@liftedanalytics.com" className="text-primary hover:underline">info@liftedanalytics.com</a></p>
                <p>Website: <a href="https://mymortgagecanada.app" className="text-primary hover:underline">mymortgagecanada.app</a></p>
              </div>
            </Section>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-border bg-background">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <Link to="/" className="flex items-center gap-2">
              <Home className="h-5 w-5 text-primary" />
              <span className="font-heading font-semibold">My Mortgage Canada</span>
            </Link>
            <p className="text-sm text-muted-foreground">
              &copy; 2025 My Mortgage Canada. Empowering Canadian homeowners.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
